import { CheckCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Alert, Button, Switch, Typography } from 'antd';
import { EditorState } from 'draft-js';
import { Formik } from 'formik';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import Modal from '../../../foundation/components/modal/Modal';
import RichTextEditor, {
  editorStateToHtml,
  htmlToEditorState,
} from '../../../foundation/components/rich_text_editor/RichTextEditor';
import { useModalForm } from '../../../foundation/hooks';
import { VARIANT_STAGE_TYPES } from '../constants';
import { useVariantAccess } from '../hooks/useVariantAccess';
import { selectVariantLock } from '../redux';
import { argumentFormValidationSchema } from '../validation_schemas/argument_validation_schema';
import EvidenceDisplay from './EvidenceDisplay';
import { StructuredArgument } from './helpers/structureArgumentsData';

const { Text } = Typography;

type ArgumentModalType = {
  closeModal: () => void;
  argument: StructuredArgument;
  source: StructuredArgument[];
  updateSource: React.Dispatch<React.SetStateAction<StructuredArgument[]>>;
};

const ArgumentModal = ({
  closeModal,
  argument,
  source,
  updateSource,
}: ArgumentModalType) => {
  const lock = useSelector(selectVariantLock);
  const { canInteract, isReadOnly } = useVariantAccess();
  const { handleModalClose, createModalOpts } = useModalForm({
    onClose: closeModal,
    stageType: VARIANT_STAGE_TYPES.INTERPRETATION,
  });

  const modalOpts = {
    ...createModalOpts(null),
    zIndex: 1010,
  };

  const handleFormSubmit = (values: {
    comment?: string;
    is_argument_met: boolean;
  }) => {
    const finalSource = source.map((item) => {
      // same section + same ACMG rule → deselect and mark as unmet others
      // only if the current argument is being marked as "Met"
      if (
        values.is_argument_met &&
        item.section === argument.section &&
        item.acmg_rule_code === argument.acmg_rule_code &&
        item.id !== argument.id
      ) {
        return { ...item, selected: false, is_argument_met: false };
      }

      if (item.id === argument.id) {
        return {
          ...item,
          comment: values.comment,
          is_argument_met: values.is_argument_met,
          selected: values.is_argument_met,
        };
      }
      return item;
    });

    updateSource(finalSource);

    handleModalClose();
  };

  const [commentEditorState, setCommentEditorState] = useState<EditorState>(
    () => htmlToEditorState(argument.comment || ''),
  );

  const handleEditorChange = useCallback(
    (newState: EditorState, handleChange: (e: any) => void) => {
      setCommentEditorState(newState);
      const htmlContent = editorStateToHtml(newState);
      handleChange({
        target: {
          name: 'comment',
          value: htmlContent,
        },
      });
    },
    [],
  );

  return (
    <Modal {...modalOpts} className="argument-modal">
      {isReadOnly && lock && (
        <Alert
          message="Read-Only Mode"
          description={`This variant is currently locked by ${lock.locked_by} and cannot be edited.`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Formik
        initialValues={{
          comment: argument.comment,
          is_argument_met: argument.is_argument_met ?? true,
        }}
        onSubmit={handleFormSubmit}
        validationSchema={argumentFormValidationSchema}
      >
        {({
          errors,
          handleChange,
          handleSubmit,
          handleBlur,
          setFieldValue,
          values,
          isValid,
          dirty,
        }) => (
          <form onSubmit={handleSubmit}>
            <div className="argument-modal__classification-container">
              <span
                className={` classification-margin-right ${`classification-${argument.acmg_class.toLowerCase()}`}`}
              >
                {argument.acmg_class.toLowerCase()}
              </span>
              <span
                className={`classification-margin-right ${`classification-${argument.acmg_class.toLowerCase()}`}`}
              >
                {argument.acmg_grade.toLowerCase()}
              </span>

              <span className={`argument-modal__classification-rule-code`}>
                {argument.acmg_rule_code}
              </span>
            </div>
            <div className="argument-modal__argument-text">Argument</div>
            <div className="argument-modal__argument-name">
              <span className="argument-modal__argument-name-icon">
                <CheckCircleOutlined />
              </span>
              <span>{argument.text}</span>
            </div>
            {argument.evidencedata &&
              Object.keys(argument.evidencedata).length > 0 && (
                <div className="argument-modal__evidence">
                  <div className="argument-modal__argument-text argument-modal__argument-text--field">
                    Evidence
                  </div>
                  <EvidenceDisplay evidence={argument.evidencedata} />
                </div>
              )}
            <div className="argument-modal__met-toggle">
              <Switch
                checkedChildren="Met"
                unCheckedChildren="Unmet"
                checked={values.is_argument_met}
                onChange={(checked) =>
                  setFieldValue('is_argument_met', checked)
                }
                disabled={!canInteract}
              />
              {!values.is_argument_met && (
                <div className="argument-modal__unmet-hint">
                  <InfoCircleOutlined style={{ marginRight: 8 }} />
                  <Text type="secondary">
                    not used to calculate posterior probability
                  </Text>
                </div>
              )}
            </div>
            <div className="c-form-field">
              <div className="argument-modal__argument-text argument-modal__argument-text--field">
                Comment
              </div>
              <div className="c-form-field__wrapper">
                <RichTextEditor
                  name="comment"
                  editorState={commentEditorState}
                  onChange={(newState) =>
                    handleEditorChange(newState, handleChange)
                  }
                  onBlur={() => handleBlur('comment')}
                  placeholder=""
                  label=""
                  readOnly={!canInteract}
                />
                <div className="c-form-field__error">
                  {errors.comment ? errors.comment : undefined}
                </div>
              </div>
            </div>
            <div className="c-form__btn-wrapper argument-modal__form-btn-wrapper">
              <Button
                type="primary"
                htmlType="submit"
                className="argument-modal__form-submit-btn"
                disabled={!(isValid && dirty) || !canInteract}
              >
                Confirm
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </Modal>
  );
};

export default ArgumentModal;
